import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { Loading } from "@/components/shared/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLead, useCustomers } from "@/api/hooks";
import { api } from "@/api/client";
import { apiErrorMessage } from "@/lib/apiError";
import { useApp } from "@/context/AppContext";
import { CustomerOnboardingDialog } from "@/components/sales/CustomerOnboardingDialog";
import { ArrowLeft, Mail, Phone, MessageCircle, XCircle, Pencil, Save, Loader2, AlertTriangle, Plus } from "lucide-react";

// Phase 4.7 — Lead disqualification. Plain-language reasons map to the
// backend's structured reasonCode (services/commercial.py:
// DISQUALIFICATION_REASON_CODES), via the real transition() engine
// (POST /leads/{id}/transition) — never an arbitrary status PATCH.
const DISQUALIFY_REASONS = [
  { key: "duplicate", label: "Duplicate enquiry", reasonCode: "DUPLICATE" },
  { key: "not_real", label: "Not a real enquiry", reasonCode: "NOT_A_REAL_ENQUIRY" },
  { key: "wrong_contact", label: "Wrong contact / can't verify", reasonCode: "WRONG_CONTACT" },
  { key: "out_of_scope", label: "Out of scope for us", reasonCode: "OUT_OF_SCOPE" },
  { key: "service", label: "Service not available", reasonCode: "SERVICE_NOT_AVAILABLE" },
  { key: "spam", label: "Spam", reasonCode: "SPAM" },
  { key: "no_response", label: "No response after repeated attempts", reasonCode: "NO_RESPONSE" },
  { key: "other", label: "Other", reasonCode: "OTHER" },
];

// Phase 4.6 — lead-stage workspace: a Lead has not yet become an Inquiry, so
// the full commercial workspace (requirements/rates/quotation) does not
// apply yet. This screen shows the lead as-is, then hands off straight into
// the unified Inquiry workspace (Workspace.jsx) — Sales never has to touch a
// separate "pipeline board" step in between.
//
// Item 2 (Aug 2026 Sales dashboard scope pass) — the manual "Qualify &
// convert" DATA-ENTRY FORM (mode/direction/origin/destination/incoterm/
// commodity/pieces/weight/cargo-ready-date) is removed: AI now captures
// full requirement detail from the lead's message/calls automatically
// (item 3), so re-typing it here duplicated that capture. The conversion
// CAPABILITY itself is kept — POST /leads/{id}/convert is still the only
// path that creates an Inquiry from a Lead (backend unchanged) — reduced
// to the one piece of information that genuinely requires a human decision:
// which onboarded customer account this lead belongs to. The lead's
// captured message (lead.interest) is carried into the new inquiry as
// customerNotes/description so nothing typed by the customer is lost.
const EMPTY_QUAL = { clientId: "", clientName: "" };

export default function LeadWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openComposer } = useApp();
  const { data: lead, isLoading, refetch } = useLead(id);
  const { data: customers } = useCustomers();
  const [qual, setQual] = useState(EMPTY_QUAL);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [disqualifying, setDisqualifying] = useState(false);
  const [disqReason, setDisqReason] = useState(null);
  const [disqBusy, setDisqBusy] = useState(false);
  const [disqErr, setDisqErr] = useState(null);
  const [editingContact, setEditingContact] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [savingContact, setSavingContact] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  if (isLoading || !lead) return <Loading />;

  const editContact = () => { setContact({ name: lead.contactName || "", email: lead.contactEmail || "", phone: lead.contactPhone || "" }); setEditingContact(true); };
  const saveContact = async () => {
    setSavingContact(true);
    try { await api.patch(`/leads/${lead.id}/contact-info`, { contactName: contact.name, contactEmail: contact.email, contactPhone: contact.phone, expectedVersion: lead.version }); await refetch(); setEditingContact(false); toast.success("Lead contact details saved"); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not save contact details")); }
    finally { setSavingContact(false); }
  };
  const reachOut = (channel) => {
    const destination = channel === "email" ? lead.contactEmail : lead.contactPhone;
    if (!destination) { editContact(); return; }
    const party = `${lead.contactName || lead.company || "Lead"} · ${destination}`;
    openComposer({ channel, leadId: lead.id, companyName: lead.company, parties: [party], defaultParty: party, subjectHint: `[${lead.id}] ${lead.company || "New enquiry"}`, intent: `make first contact and qualify lead ${lead.id}` });
  };

  const disqualify = async () => {
    if (!disqReason) return;
    setDisqBusy(true); setDisqErr(null);
    try {
      await api.post(`/leads/${lead.id}/transition`, {
        toStatus: "DISQUALIFIED", expectedVersion: lead.version, reasonCode: disqReason.reasonCode, reason: disqReason.label,
      });
      toast.success("Lead disqualified.");
      await refetch();
      setDisqualifying(false);
    } catch (e) {
      setDisqErr(apiErrorMessage(e));
    } finally {
      setDisqBusy(false);
    }
  };

  const convert = async () => {
    setBusy(true); setErr(null);
    try {
      // Item 2 — no manual shipment-detail form: the lead's own captured
      // message (item 3) already carries the requirement, so it's passed
      // straight through as the new inquiry's notes/description.
      const { data } = await api.post(`/leads/${lead.id}/convert`, {
        qualification: { ...qual, customerNotes: lead.interest, description: lead.interest },
      });
      toast.success("Converted to Inquiry");
      // Aug 2026 IA correction: land straight on the customer's page with
      // the new inquiry pre-selected — never a standalone inquiry page.
      // The convert response already carries the inquiry's clientId (it's
      // the same account just selected in qualification above).
      const newClientId = data.inquiry?.clientId;
      navigate(newClientId ? `/sales/customers/${newClientId}?inquiry=${data.inquiry.id}` : `/sales/inquiries/${data.inquiry.id}`);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (lead.convertedInquiryId) {
    navigate(`/sales/inquiries/${lead.convertedInquiryId}`, { replace: true });
    return <Loading />;
  }

  return (
    <>
      <PageHeader
        title={<span className="flex items-center gap-2"><button onClick={() => navigate(-1)} data-testid="lead-back" className="text-slate-400 hover:text-slate-700"><ArrowLeft className="h-4 w-4" /></button>{lead.company || lead.id}</span>}
        subtitle={`${lead.id} · via ${lead.source} · Owner ${lead.owner || "—"}`}
        actions={<div className="flex items-center gap-1.5">
          <StatusPill status={lead.status} />
          {lead.status !== "DISQUALIFIED" && (
            <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDisqualifying(true)} data-testid="disqualify-lead-btn">
              <XCircle className="h-3.5 w-3.5 mr-1.5" />Disqualify
            </Button>
          )}
        </div>}
      />
      <div className="mx-auto w-full max-w-4xl flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Contact &amp; outreach</div>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={editContact} data-testid="lead-edit-contact"><Pencil className="h-3 w-3 mr-1" />Edit contact</Button>
          </div>
          {editingContact ? (
            <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="lead-contact-form">
              <Input placeholder="Contact name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="lead-contact-name" />
              <Input type="email" placeholder="Email address" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="lead-contact-email" />
              <Input type="tel" placeholder="Phone / WhatsApp" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="lead-contact-phone" />
              <div className="sm:col-span-3 flex justify-end gap-2"><Button size="sm" variant="ghost" onClick={() => setEditingContact(false)}>Cancel</Button><Button size="sm" onClick={saveContact} disabled={savingContact || (!contact.email && !contact.phone)} data-testid="lead-save-contact">{savingContact ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}Save contact</Button></div>
            </div>
          ) : <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div><div className="text-slate-400 text-[11px]">Name</div><div>{lead.contactName || "—"}</div></div>
            <div><div className="text-slate-400 text-[11px]">Email</div><div>{lead.contactEmail || "—"}</div></div>
            <div><div className="text-slate-400 text-[11px]">Phone</div><div>{lead.contactPhone || "—"}</div></div>
            <div><div className="text-slate-400 text-[11px]">Received</div><div>{(lead.createdAt || "").slice(0, 10) || "—"}</div></div>
          </div>}
          {!lead.contactEmail && !lead.contactPhone && !editingContact && <button onClick={editContact} className="mt-3 w-full rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-left text-xs text-amber-800 flex gap-2" data-testid="lead-missing-contact"><AlertTriangle className="h-4 w-4 shrink-0" /><span><b>Contact details are missing.</b> Add an email address or phone number to reach this lead from the dashboard.</span></button>}
          {/* Item 3 — the customer's own message/inquiry text, captured at
              lead intake (NewLeadDialog) and stored on lead.interest,
              surfaced prominently here (previously captured but never
              displayed anywhere on this screen). */}
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2.5" data-testid="lead-message">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Customer's message / inquiry</div>
            <div className="mt-0.5 text-sm text-slate-700 whitespace-pre-wrap">{lead.interest || "No message captured for this lead yet."}</div>
          </div>
          <div className="flex items-center gap-1.5 mt-3">
            <Button size="sm" variant="outline" className="h-8" onClick={() => reachOut("call")} disabled={!lead.contactPhone} title={!lead.contactPhone ? "Add a phone number first" : "Log a call"} data-testid="lead-call"><Phone className="h-3.5 w-3.5 mr-1.5" />Call</Button>
            <Button size="sm" variant="outline" className="h-8" onClick={() => reachOut("whatsapp")} disabled={!lead.contactPhone} title={!lead.contactPhone ? "Add a phone number first" : "Send WhatsApp"} data-testid="lead-whatsapp"><MessageCircle className="h-3.5 w-3.5 mr-1.5" />WhatsApp</Button>
            <Button size="sm" variant="outline" className="h-8" onClick={() => reachOut("email")} disabled={!lead.contactEmail} title={!lead.contactEmail ? "Add an email address first" : "Send email"} data-testid="lead-email"><Mail className="h-3.5 w-3.5 mr-1.5" />Email</Button>
          </div>
        </div>

        {lead.status === "DISQUALIFIED" ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="lead-disqualified-panel">
            <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm"><XCircle className="h-4 w-4 text-slate-400" />Lead Disqualified</div>
            <div className="text-[13px] text-slate-500 mt-1">
              Reason: {lead.disqualificationReasonCode || "Not specified"}
              {lead.decidedAt && <> · {(lead.decidedAt || "").slice(0, 10)} · {lead.decidedBy}</>}
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-500">What to do next</div>
              <div className="text-sm font-semibold text-indigo-900 mt-0.5">Convert this lead to an Inquiry.</div>
              <div className="text-[12px] text-indigo-700 mt-0.5">The captured message above carries the requirement — select the customer account and convert.</div>
            </div>

            {disqualifying && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 space-y-2" data-testid="disqualify-panel">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-red-600">Disqualify this lead</div>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {DISQUALIFY_REASONS.map((r) => (
                    <button key={r.key} type="button" onClick={() => setDisqReason(r)} data-testid={`disqualify-reason-${r.key}`}
                      className={`text-left text-[12.5px] rounded-lg border px-2.5 py-2 transition-colors bg-white ${disqReason?.key === r.key ? "border-red-400 text-red-800" : "border-slate-200 text-slate-600"}`}>
                      {r.label}
                    </button>
                  ))}
                </div>
                {disqErr && <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">{disqErr}</div>}
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setDisqualifying(false); setDisqReason(null); setDisqErr(null); }}>Cancel</Button>
                  <Button size="sm" disabled={disqBusy || !disqReason} onClick={disqualify} data-testid="confirm-disqualify-btn" className="bg-red-600 hover:bg-red-700">
                    {disqBusy ? "Working…" : "Confirm disqualify"}
                  </Button>
                </div>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Convert to Inquiry</div>
              <label className="block text-xs text-slate-500">Onboarded customer account<select value={qual.clientId} onChange={(event) => { const customer = (customers || []).find((item) => item.id === event.target.value); setQual((current) => ({ ...current, clientId: customer?.id || "", clientName: customer?.name || "" })); }} className="mt-1 h-9 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="qual-clientId"><option value="">Select customer</option>{(customers || []).map((customer) => <option key={customer.id} value={customer.id}>{customer.name} · {customer.id}</option>)}</select><button type="button" onClick={() => setOnboardingOpen(true)} className="mt-1.5 inline-flex items-center text-xs font-medium text-blue-700 hover:underline"><Plus className="mr-1 h-3 w-3" />Customer not listed? Onboard now</button></label>
              {err && <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">{err}</div>}
              <Button disabled={busy || !qual.clientId} onClick={convert} data-testid="convert-lead-btn">Convert to Inquiry</Button>
            </div>
          </>
        )}
      </div>
      <CustomerOnboardingDialog open={onboardingOpen} onOpenChange={setOnboardingOpen} onCreated={(customer) => setQual((current) => ({ ...current, clientId: customer.id, clientName: customer.name }))} />
    </>
  );
}
